import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useMerchantStore } from "../../store/merchantStore";
import { MapPin, X } from "lucide-react";
import BookingPopup from "./bookingPopup";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { PractitionerDetails } from "../../components/interfaces";
import Loading from "../../components/loader";
import {
  Clock,
  Calendar,
  CheckCircle2,
  Info,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";
import he from "he";
import { API } from "../../utils/axios";
import toast from "react-hot-toast";

const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY as string,
);

export default function ServiceDetail() {
  let params = useParams();
  let id: any = params.id;
  const [merchant, setMerchant] = useState<any>();
  const [userSelectedPractitionerId, setUserSelectedPractitionerId] =
    useState("");
  const selectedServiceData = useMerchantStore((state) => state.merchant);
  const [selectedImage, setSelectedImage] = useState("");
  const [practitioners, setPractitioners] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPractitioner, setSelectedPractitioner] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getSpecificBusinessById = async () => {
    await API(`/business/${id}`)
      .then((res) => {
        setMerchant(res.data);
        if (res.data?.businessPhotos) {
          setSelectedImage(res.data?.businessPhotos[0]);
        }
      })
      .catch((err) => {
        console.error("Error Fetching Data: getSpecificBusinessById", err);
      });
  };

  const getPractitionersByBusinessId = async () => {
    if (!merchant?.id) {
      return;
    }
    try {
      setIsLoading(true);
      const utcNow = new Date().toISOString();

      const res = await API.get(
        `/practitioner/${merchant.id}/practitioner/avalability?start=${utcNow}`,
      )
      setPractitioners(res.data.data)
    } catch (error) {
      console.log("Error in getPractitionersByBusinessId", error);
      toast.error("Failed to load practitioners")
    } finally {
      setIsLoading(false);
    }
  };

  const servicesObject = {
    "1": "Physiotherapy",
    "2": "Chiropractic",
    "3": "Acupuncture",
    "4": "Massage Therapy",
  };

  type ServiceKey = keyof typeof servicesObject;

  const mapServices =
    merchant?.services && Array.isArray(merchant.services)
      ? merchant.services
          .map((key: any) => servicesObject[key as ServiceKey])
          .filter((val: any): val is string => Boolean(val))
      : [];

  const openModal = (practitioner: any) => {
    setSelectedPractitioner(practitioner);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPractitioner(null);
  };

  const formatSlot = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth();

    const time = date.toLocaleString("en-GB", {
    // day: "2-digit",
    // month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "UTC",
  });
    const day = date.toLocaleDateString([], { month: "short", day: "numeric" });

    return isToday ? `Today ${time}` : `${day} ${time}`;
  };

  useEffect(() => {
    getPractitionersByBusinessId();
  }, [merchant]);

  useEffect(() => {
    if (id) {
      getSpecificBusinessById();
    } else {
      setMerchant(selectedServiceData);
      if (selectedServiceData?.businessPhotos) {
        setSelectedImage(selectedServiceData?.businessPhotos?.[0]);
      }
    }
  }, [id]);

  useEffect(() => {
    if (selectedServiceData?.practitioners?.length) {
      setUserSelectedPractitionerId(
        selectedServiceData?.practitioners?.[0]?.id,
      );
    }
  }, [selectedServiceData]);

  return (
    <div className="min-h-screen bg-[#f7faf9] py-6 sm:py-10 px-4 sm:px-6">
      <div
        className="
      max-w-7xl mx-auto gap-16 bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden
      grid grid-cols-1 lg:grid-cols-12
      px-4 sm:px-6 lg:px-8 py-4 sm:py-6
    "
      >
        {/* LEFT SECTION */}
        <div className="lg:col-span-7 flex flex-col gap-8 sm:gap-10">
          {/* 1. Header Section */}
          <div className="space-y-4 sm:space-y-5">
            {/* Badges */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {merchant?.approval_type === "auto" ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100/50 border border-emerald-200 text-emerald-800 text-xs font-bold uppercase tracking-wider">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Instant Booking
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100/50 border border-amber-200 text-amber-800 text-xs font-bold uppercase tracking-wider">
                  <Clock className="w-3.5 h-3.5" /> Request Required
                </span>
              )}
              <span className="px-3 py-1 rounded-full bg-white border border-stone-200 text-stone-600 text-xs font-bold uppercase tracking-wider shadow-sm">
                {merchant?.businessType}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl sm:text-2xl md:text-3xl text-zinc-900 leading-tight tracking-tight">
              {merchant?.businessAs
                ? he.decode(merchant.businessAs)
                : merchant?.businessName
                  ? he.decode(merchant.businessName)
                  : "No Business name"}
            </h1>

            {/* Meta */}
            <div className="my-4 flex flex-col gap-0.5">
              <div className="flex items-center gap-1.5 text-gray-900">
                <MapPin className="h-5 w-5 shrink-0 text-emerald-500" />
                <span className="text-lg font-semibold truncate">
                  {merchant?.cityData?.city_name},{" "}
                  {merchant?.stateData?.state_name}
                </span>
              </div>
              <span className="pl-5 text-base text-gray-400 truncate font-medium">
                {merchant?.merchantAddress
                  ? he.decode(merchant.merchantAddress)
                  : "address not available"}
              </span>
            </div>
            {/* <div className="flex flex-wrap items-center gap-y-2 gap-x-4 text-sm text-zinc-500 font-medium">
              <div className="flex items-center gap-2 group cursor-pointer hover:text-violet-700 transition-colors">
                <div className="p-1.5 bg-white rounded-full shadow-sm ring-1 ring-stone-200 group-hover:ring-violet-200">
                  <MapPin className="w-3.5 h-3.5" />
                </div>
                <span className="border-b border-transparent group-hover:border-violet-300">
                  {merchant?.merchantAddress
                    ? he.decode(merchant.merchantAddress)
                    : "Location unavailable"}
                </span>
              </div>
            </div> */}
          </div>

          {/* 2. Hero Image */}
          <div className="relative aspect-[4/3] sm:aspect-[16/9] lg:aspect-[2/1] w-full overflow-hidden rounded-2xl shadow-xl ring-1 ring-black/5">
            {merchant?.businessPhotos ? (
              <img
                src={merchant.businessPhotos}
                alt="Business"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105"
              />
            ) : (
              <div className="absolute inset-0 bg-stone-100 flex items-center justify-center text-stone-400">
                <span className="text-lg">Image not available</span>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          </div>

          {/* 3. Services */}
          {mapServices?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {mapServices.map((tag: string, idx: number) => (
                <span
                  key={idx}
                  className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-xs sm:text-sm
                         bg-white border border-stone-200 text-stone-600 shadow-sm"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* 4. Description */}
          <div className="space-y-3 sm:space-y-4">
            <h3 className="text-lg sm:text-xl font-semibold text-zinc-900 flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-500" />
              About the Business
            </h3>
            <p className="text-base sm:text-lg leading-relaxed max-w-2xl">
              {merchant?.business_desc
                ? he.decode(merchant.business_desc)
                : "No description provided."}
            </p>
          </div>
        </div>

        {/* RIGHT SECTION */}
        <div className="lg:col-span-5 flex flex-col mt-10 lg:mt-0">
          <div className="relative lg:sticky lg:top-8">
            <div
              className="
            bg-white/70 backdrop-blur-xl
            rounded-[1.5rem] sm:rounded-[2rem]
            p-4 sm:p-6 lg:p-8
            shadow-lg border border-stone-300
          "
            >
              {/* Header */}
              <div className="mb-6 sm:mb-8 flex items-end justify-between">
                <div>
                  <h2 className="text-xl sm:text-2xl text-[#333333]">
                    Book a Practitioner
                  </h2>
                  <p className="text-stone-500 text-sm mt-1">
                    Choose a professional to see availability
                  </p>
                </div>
                <div className="hidden sm:flex h-10 w-10 items-center justify-center rounded-full bg-violet-50 text-violet-600">
                  <Calendar className="w-5 h-5" />
                </div>
              </div>

              {/* Practitioner List */}
              <div className="space-y-3 sm:space-y-4">
                {isLoading ? (
                  <Loading size={80} />
                ) : practitioners?.length > 0 ? (
                  practitioners.map(
                    (practitioner: PractitionerDetails, index: number) => {
                      const nextSlotObj = practitioner?.availabilities?.[0];
                      const nextSlotText = nextSlotObj
                        ? formatSlot(nextSlotObj.slot_start)
                        : null;

                      return (
                        <div
                          key={index}
                          onClick={() =>
                            nextSlotText && openModal(practitioner)
                          }
                          className="
                      group rounded-xl sm:rounded-2xl border p-3 sm:p-4
                      bg-white transition-all duration-300
                      hover:border-violet-400 hover:shadow-md cursor-pointer
                    "
                        >
                          <div className="flex items-center gap-3 sm:gap-4">
                            {/* Avatar */}
                            <div className="shrink-0">
                              {practitioner?.profilePicture ? (
                                <img
                                  src={practitioner.profilePicture}
                                  alt={practitioner.practitionerName}
                                  className="h-12 w-12 sm:h-14 sm:w-14 rounded-full object-cover ring-2 ring-white shadow"
                                />
                              ) : (
                                <div className="h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-stone-200 flex items-center justify-center text-xs text-stone-600">
                                  No image
                                </div>
                              )}
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-base sm:text-lg truncate group-hover:text-violet-700">
                                {practitioner?.practitionerName}
                              </h3>
                              {nextSlotText ? (
                                <span className="inline-block mt-1 text-xs font-medium text-violet-700 bg-violet-50 px-2 py-0.5 rounded-md">
                                  Next: {nextSlotText}
                                </span>
                              ) : (
                                <span className="text-xs text-stone-400">
                                  Slots not available
                                </span>
                              )}
                            </div>

                            {nextSlotText && (
                              <div className="hidden sm:block">
                                <div className="p-2 rounded-full bg-violet-600 text-white">
                                  <ArrowRight className="w-4 h-4" />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    },
                  )
                ) : (
                  <div className="text-center py-8 rounded-xl border border-dashed border-stone-300 bg-stone-50">
                    <p className="text-stone-500 text-sm">
                      No practitioners available at this moment.
                    </p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="mt-6 pt-5 border-t border-stone-100 flex items-center justify-between text-xs text-stone-400">
                <div className="flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-500" />
                  <span>Secure Booking</span>
                </div>
                <span>Powered by Stripe</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL */}
      <Elements stripe={stripePromise}>
        <BookingPopup
          isOpen={isModalOpen}
          onClose={closeModal}
          merchant={merchant}
          selectedPractitioner={selectedPractitioner}
        />
      </Elements>
    </div>
  );
}
